/**
 * @module @shared/api/identity-api
 * HTTP-Datenschicht für den service-role-gestützten Identity-Admin-Worker
 * (`identity-admin-api`). Der Worker verbindet `auth.users` (Email/Provider) mit
 * `identity.user_profiles` (Username) zu einem `AdminUser` und erlaubt das
 * Setzen/Ändern interner Usernamen — Funktionen, die die SPA mit ihrem
 * publishable Key nicht selbst ausführen darf.
 *
 * Basis-URL: `env.identityApiUrl` (`VITE_IDENTITY_API_URL`). Jeder Request schickt
 * den aktuellen Supabase-Access-Token als `Authorization: Bearer <token>`; der
 * Worker verifiziert Token + Plattform-Admin serverseitig.
 *
 * Robustheit: Ist keine Worker-URL konfiguriert (und kein Dev-Bypass aktiv),
 * liefern die Lese-Funktionen neutrale Leerergebnisse statt zu werfen — die
 * Admin-App rendert so auch ohne erreichbaren Worker (sauberer Empty-State).
 */
import { z } from 'zod';
import { AdminUserSchema, type AdminUser, type SetUsernameRequest } from '@repo/shared-types';
import { env } from '@shared/config';
import { supabase } from './supabase';

/** Standard-Limit der Nutzersuche, falls der Aufrufer keines angibt. */
const DEFAULT_SEARCH_LIMIT = 20;

/** Antwort von `GET /api/users` — Liste aufgelöster Nutzer. */
const UsersListResponseSchema = z.object({ users: z.array(AdminUserSchema) });

/** Antwort von `POST /api/users/resolve` — Map UUID → aufgelöster Nutzer. */
const ResolveResponseSchema = z.object({ users: z.record(z.string(), AdminUserSchema) });

/** Ist eine Worker-Basis-URL konfiguriert? */
function isWorkerConfigured(): boolean {
  return env.identityApiUrl.trim().length > 0;
}

/** Holt den aktuellen Bearer-Access-Token (oder `undefined`, falls keine Session). */
async function getAccessToken(): Promise<string | undefined> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? undefined;
}

/**
 * Versucht, aus einer Fehlerantwort eine menschenlesbare Meldung zu extrahieren
 * (`{ error }` oder `{ message }`), fällt auf den Body-Text bzw. den Statuscode zurück.
 *
 * @param res Die fehlgeschlagene `Response` (status >= 400).
 * @returns Eine möglichst sprechende Fehlermeldung.
 */
async function extractErrorMessage(res: Response): Promise<string> {
  try {
    const payload: unknown = await res.clone().json();
    if (payload && typeof payload === 'object') {
      const record = payload as Record<string, unknown>;
      const candidate = record.error ?? record.message;
      if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate;
    }
  } catch {
    // Kein JSON-Body — unten auf Text/Status zurückfallen.
  }
  const text = await res.text().catch(() => '');
  if (text.trim().length > 0) return text;
  return `Identity-API-Fehler ${res.status} ${res.statusText}`.trim();
}

/**
 * Führt einen authentifizierten Request gegen den Identity-Admin-Worker aus.
 * Hängt `env.identityApiUrl` als Basis voran, setzt den Bearer-Token und wirft
 * bei `!res.ok` mit einer geparsten Fehlermeldung.
 *
 * @typeParam T Erwarteter (roher) JSON-Antworttyp — der Aufrufer validiert mit Zod.
 * @param path Pfad inkl. führendem `/` (z. B. `/api/users`).
 * @param init Optionale `fetch`-Optionen (Methode, Body, Header).
 * @returns Das geparste JSON der Antwort als `T`.
 * @throws Error wenn keine Worker-URL konfiguriert ist oder der Request scheitert.
 */
export async function identityApiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (!isWorkerConfigured()) {
    throw new Error('Identity-Worker nicht konfiguriert (VITE_IDENTITY_API_URL fehlt).');
  }
  const baseUrl = env.identityApiUrl.trim().replace(/\/$/, '');
  const token = await getAccessToken();
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  if (init?.body !== undefined && init.body !== null) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${baseUrl}${path}`, { ...init, headers });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return (await res.json()) as T;
}

/**
 * Sucht Nutzer über Email/Username/Anzeigename. Leere `query` liefert die neuesten
 * Nutzer. Ist kein Worker konfiguriert, wird eine leere Liste zurückgegeben.
 *
 * @param query Freitext-Suchbegriff (kann leer sein).
 * @param limit Maximale Trefferzahl (Default `20`).
 * @returns Liste aufgelöster `AdminUser` (validiert).
 */
export async function searchUsers(
  query: string,
  limit: number = DEFAULT_SEARCH_LIMIT,
): Promise<AdminUser[]> {
  if (!isWorkerConfigured()) return [];
  const params = new URLSearchParams();
  const trimmed = query.trim();
  if (trimmed.length > 0) params.set('query', trimmed);
  params.set('limit', String(limit));
  const json = await identityApiFetch<unknown>(`/api/users?${params.toString()}`);
  return UsersListResponseSchema.parse(json).users;
}

/**
 * Löst eine Menge von Nutzer-UUIDs im Batch zu `AdminUser` auf (für Audit/Tabellen).
 * Leere Eingabe oder fehlender Worker ergeben eine leere Map.
 *
 * @param ids Zu auflösende Nutzer-UUIDs.
 * @returns Map `userId → AdminUser` (validiert); fehlende IDs sind nicht enthalten.
 */
export async function resolveUsers(ids: string[]): Promise<Record<string, AdminUser>> {
  if (ids.length === 0 || !isWorkerConfigured()) return {};
  const json = await identityApiFetch<unknown>('/api/users/resolve', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  return ResolveResponseSchema.parse(json).users;
}

/**
 * Lädt einen einzelnen Nutzer per UUID. Ist kein Worker konfiguriert, wird `null`
 * zurückgegeben (neutraler Empty-State statt Crash).
 *
 * @param id Nutzer-UUID.
 * @returns Der aufgelöste `AdminUser` oder `null`.
 */
export async function getUser(id: string): Promise<AdminUser | null> {
  if (!isWorkerConfigured()) return null;
  const json = await identityApiFetch<unknown>(`/api/users/${encodeURIComponent(id)}`);
  return AdminUserSchema.parse(json);
}

/**
 * Setzt bzw. ändert den internen Username eines Nutzers (schreibt serverseitig
 * `identity.audit_log` mit Action `username.set`). Ist kein Worker konfiguriert,
 * wirft die Funktion — eine Schreibaktion darf nicht stillschweigend verpuffen.
 *
 * @param id Nutzer-UUID.
 * @param body Gewünschter Username (+ optionaler Anzeigename).
 * @returns Der aktualisierte `AdminUser` (validiert).
 * @throws Error bei Dublette (`409`) oder fehlendem Worker.
 */
export async function setUsername(id: string, body: SetUsernameRequest): Promise<AdminUser> {
  const json = await identityApiFetch<unknown>(`/api/users/${encodeURIComponent(id)}/username`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return AdminUserSchema.parse(json);
}
